type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const MIN_LEVEL: LogLevel = import.meta.env.PROD ? 'warn' : 'debug';

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LEVEL];
}

function formatMessage(level: LogLevel, message: string, context?: LogContext): string {
  const timestamp = new Date().toISOString();
  const contextStr = context ? ` ${JSON.stringify(context)}` : '';
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'Unknown error';
}

export const logger = {
  debug(message: string, context?: LogContext): void {
    if (shouldLog('debug')) console.debug(formatMessage('debug', message, context));
  },
  info(message: string, context?: LogContext): void {
    if (shouldLog('info')) console.info(formatMessage('info', message, context));
  },
  warn(message: string, context?: LogContext): void {
    if (shouldLog('warn')) console.warn(formatMessage('warn', message, context));
  },
  error(message: string, error?: unknown, context?: LogContext): void {
    if (shouldLog('error')) {
      const errorMsg = error !== undefined ? getErrorMessage(error) : undefined;
      console.error(formatMessage('error', message, { ...context, ...(errorMsg ? { error: errorMsg } : {}) }));
    }
  },
};
